import { Stack, Typography, useTheme } from "@mui/material";
import { Settings } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { AppRouting } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { SettingsMenuSection } from "web_myplann/More/Components/SettingsMenuSection";
import { useSettingsMenuItems } from "web_myplann/More/Hooks/useSettingsMenuItems";
import { MissingAdvisor } from "web_myplann/Profile/Components/MissingAdvisor";
import { ProfileAvatar } from "web_myplann/Profile/Components/ProfileAvatar";
import { ProfileDetailsList } from "web_myplann/Profile/Components/ProfileDetailsList";
import { ProfileSection } from "web_myplann/Profile/Components/ProfileSection";
import { useProfileOverview } from "web_myplann/Profile/Hooks/useProfileOverview";
import { useProfilePhotoUpload } from "web_myplann/Profile/Hooks/useProfilePhotoUpload";
import { MyPlannLoading } from "web_myplann/Shared/Components/MyPlannLoading";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import { MyPlannNavigationHeader } from "web_myplann/Shared/Header/MyPlannNavigationHeader";

const R = Resources.Profile;

export const ProfileV2Page: FC = () => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();
	const { toolItems } = useSettingsMenuItems();
	const { changeProfilePhoto, isUploadingPhoto } = useProfilePhotoUpload();
	const {
		isLoading,
		clientNotFound,
		name,
		displayName,
		photo,
		email,
		phone,
		permanentAddress,
		contactAddress,
	} = useProfileOverview();

	return (
		<MyPlannLayout
			disableContentPadding
			bgColor={theme.palette.grey[50]}
			topBar={
				<MyPlannNavigationHeader
					title={t(R.V2.title)}
					bgColor={theme.palette.background.appBar}
					defaultRoute={AppRouting.MyPlann.Dashboard.path()}
					alwaysUseDefaultRoute
					iconColor={theme.palette.grey[700]}
					endComponent={
						<Settings
							size={theme.iconSize.md}
							cursor="pointer"
							color={theme.palette.grey[700]}
							onClick={() =>
								navigate(AppRouting.MyPlann.Profile.Settings.path())
							}
						/>
					}
				/>
			}
		>
			{isLoading ? (
				<MyPlannLoading />
			) : clientNotFound ? (
				<MissingAdvisor />
			) : (
				<Stack pb={theme.spacingTokens.lg}>
					<Stack bgcolor={theme.palette.background.paper}>
						<Stack py={3.2} px={theme.spacingTokens.lg} alignItems="center">
							<ProfileAvatar
								name={name}
								photo={photo}
								isUploading={isUploadingPhoto}
								onClick={changeProfilePhoto}
								mb={1.6}
							/>
							<Typography
								variant="h2"
								align="center"
								sx={{ overflowWrap: "anywhere" }}
							>
								{displayName}
							</Typography>
						</Stack>

						<ProfileDetailsList
							email={email}
							phone={phone}
							permanentAddress={permanentAddress}
							contactAddress={contactAddress}
						/>
					</Stack>

					<ProfileSection title={t(R.Overview.toolsSection)}>
						<SettingsMenuSection items={toolItems} />
					</ProfileSection>
				</Stack>
			)}
		</MyPlannLayout>
	);
};
